import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from './ButtonExtended';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    /** Use elevated when opening above a drawer or base modal; use top when opening above an elevated modal */
    stackLevel?: 'default' | 'elevated' | 'top';
    /** Optional id on the modal panel (e.g. tour highlight). */
    panelId?: string;
}

const stackLevelClasses: Record<
    NonNullable<ModalProps['stackLevel']>,
    { z: string; backdrop: string }
> = {
    default: { z: 'z-50', backdrop: 'bg-black/70' },
    elevated: { z: 'z-[100]', backdrop: 'bg-black/60 backdrop-blur-sm' },
    top: { z: 'z-[110]', backdrop: 'bg-black/60 backdrop-blur-sm' },
};

const maxWidthClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
};

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = 'lg',
    stackLevel = 'default',
    panelId,
}) => {
    const titleId = useId();
    const { z: zClass, backdrop: backdropClass } = stackLevelClasses[stackLevel];

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className={clsx('fixed inset-0 overflow-y-auto', zClass)}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
        >
            <div
                className={clsx('fixed inset-0 transition-opacity', backdropClass)}
                onClick={onClose}
                aria-hidden="true"
            />

            <div className="pointer-events-none relative flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
                <div
                    id={panelId}
                    data-tour-panel-ready={isOpen ? 'true' : undefined}
                    className={clsx(
                        'pointer-events-auto relative z-10 flex max-h-[calc(100vh-2rem)] w-full transform flex-col overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:max-h-[calc(100vh-4rem)]',
                        maxWidthClasses[maxWidth],
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:px-6">
                        <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
                            {title}
                        </h3>
                        <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                    <div className="overflow-y-auto bg-white dark:bg-gray-800 px-4 pb-4 pt-4 sm:px-6 sm:pb-6">
                        {children}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
